import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUniversalAccess,
  faTimes,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "./context/ThemeContext";
import { useTranslation } from "../../i18n/LanguageContext";
import "./css/ADAWidget.css";

/* ── Helpers ───────────────────────────────────────────────────── */
const safeStorage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, val) {
    try {
      localStorage.setItem(key, val);
    } catch {
      /* noop */
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  },
};

const STORAGE_KEY = "ada-font-scale";
const SPACING_KEY = "ada-letter-spacing";
const LINE_HEIGHT_KEY = "ada-line-height";
const WIDGET_ENABLED_KEY = "ada-widget-enabled";

/* Each step: attribute value (null = default/removed) + display label */
const SCALES = [
  { value: null, label: "100%", description: "Default size" },
  { value: "110", label: "110%", description: "Slightly larger" },
  { value: "125", label: "125%", description: "Larger" },
  { value: "150", label: "150%", description: "Much larger" },
];

const SPACING_STEPS = [
  { value: null, label: "ada.spacing.label.default" },
  { value: "0.05em", label: "ada.spacing.label.wide" },
  { value: "0.1em", label: "ada.spacing.label.wider" },
  { value: "0.15em", label: "ada.spacing.label.widest" },
];

const LINE_HEIGHT_STEPS = [
  { value: null, label: "ada.lineHeight.label.default" },
  { value: "1.6", label: "ada.lineHeight.label.relaxed" },
  { value: "1.9", label: "ada.lineHeight.label.loose" },
  { value: "2.2", label: "ada.lineHeight.label.veryLoose" },
];

/* Map scale index value (e.g. "125") to a root font-size percentage.
   All rem-based tokens in Common-Styles.css scale automatically. */
const SCALE_TO_PCT = {
  null: "100%",
  110: "110%",
  125: "125%",
  150: "150%",
  175: "175%",
  200: "200%",
};

function applyScale(value) {
  const pct = SCALE_TO_PCT[value] ?? "100%";
  if (pct === "100%") {
    // Remove inline override so browser/user stylesheet default takes over
    document.documentElement.style.removeProperty("font-size");
  } else {
    document.documentElement.style.fontSize = pct;
  }
}

function ADAWidget() {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Enabled / dismissed state
  const [enabled, setEnabled] = useState(
    () => safeStorage.get(WIDGET_ENABLED_KEY) !== "false",
  );
  const [dismissing, setDismissing] = useState(false);

  // Listen for changes from AppearanceSettings (re-enable from settings panel)
  useEffect(() => {
    const handler = (e) => {
      setEnabled(e.detail.enabled);
      if (e.detail.enabled) setDismissing(false);
    };
    window.addEventListener("adaWidgetEnabledChanged", handler);
    return () => window.removeEventListener("adaWidgetEnabledChanged", handler);
  }, []);

  const handleDismiss = () => {
    setDismissing(true);
    setIsOpen(false);
    setTimeout(() => {
      safeStorage.set(WIDGET_ENABLED_KEY, "false");
      setEnabled(false);
      setDismissing(false);
      window.dispatchEvent(
        new CustomEvent("adaWidgetEnabledChanged", {
          detail: { enabled: false },
        }),
      );
    }, 4000);
  };

  // Initialise from localStorage
  const [scaleIndex, setScaleIndex] = useState(() => {
    const saved = safeStorage.get(STORAGE_KEY);
    if (saved === null) return 0;
    const idx = SCALES.findIndex((s) => s.value === saved);
    return idx >= 0 ? idx : 0;
  });

  // Apply scale on mount and whenever it changes
  useEffect(() => {
    applyScale(SCALES[scaleIndex].value);
    if (SCALES[scaleIndex].value === null) {
      safeStorage.remove(STORAGE_KEY);
    } else {
      safeStorage.set(STORAGE_KEY, SCALES[scaleIndex].value);
    }
  }, [scaleIndex]);

  const cycleScale = () => {
    setScaleIndex((prev) => (prev + 1) % SCALES.length);
  };

  const resetScale = () => {
    setScaleIndex(0);
  };

  // ── Letter spacing state ────────────────────────────────────
  const [spacingIndex, setSpacingIndex] = useState(() => {
    const saved = safeStorage.get(SPACING_KEY);
    if (saved === null) return 0;
    const idx = SPACING_STEPS.findIndex((s) => s.value === saved);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    const val = SPACING_STEPS[spacingIndex].value;
    if (val === null) {
      document.documentElement.style.removeProperty("letter-spacing");
      safeStorage.remove(SPACING_KEY);
    } else {
      document.documentElement.style.letterSpacing = val;
      safeStorage.set(SPACING_KEY, val);
    }
  }, [spacingIndex]);

  const cycleSpacing = () =>
    setSpacingIndex((prev) => (prev + 1) % SPACING_STEPS.length);

  // ── Line height state ───────────────────────────────────────
  const [lineHeightIndex, setLineHeightIndex] = useState(() => {
    const saved = safeStorage.get(LINE_HEIGHT_KEY);
    if (saved === null) return 0;
    const idx = LINE_HEIGHT_STEPS.findIndex((s) => s.value === saved);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    const val = LINE_HEIGHT_STEPS[lineHeightIndex].value;
    if (val === null) {
      document.documentElement.style.removeProperty("line-height");
      safeStorage.remove(LINE_HEIGHT_KEY);
    } else {
      document.documentElement.style.lineHeight = val;
      safeStorage.set(LINE_HEIGHT_KEY, val);
    }
  }, [lineHeightIndex]);

  const cycleLineHeight = () =>
    setLineHeightIndex((prev) => (prev + 1) % LINE_HEIGHT_STEPS.length);

  // ── Reset all ───────────────────────────────────────────────
  const resetAll = () => {
    setScaleIndex(0);
    setSpacingIndex(0);
    setLineHeightIndex(0);
    setTheme("light");
  };

  // ── Contrast / theme state ──────────────────────────────────
  const isDark = theme === "dark";
  const toggleContrast = () => setTheme(isDark ? "light" : "dark");

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const current = SCALES[scaleIndex];

  // Hidden — user dismissed from settings or from widget itself
  if (!enabled && !dismissing) return null;

  // Dismissing — show brief notice before fully hiding
  if (dismissing) {
    return (
      <div className="ada-fab-container">
        <div className="ada-dismissed-notice">
          <p>{t("ada.dismissed.message")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Popup panel */}
      {isOpen && (
        <div
          className="ada-window"
          role="dialog"
          aria-label={t("ada.panel.ariaLabel")}
        >
          {/* Header */}
          <div className="ada-header">
            <div className="ada-header-left">
              <FontAwesomeIcon
                icon={faUniversalAccess}
                className="ada-header-icon"
              />
              <span className="ada-header-title">{t("ada.title")}</span>
            </div>
            <div className="ada-header-actions">
              <button
                className="ada-hide-btn"
                onClick={handleDismiss}
                data-tooltip={t("ada.dismiss.preview")}
                aria-label={t("ada.dismiss.ariaLabel")}
              >
                <FontAwesomeIcon icon={faEyeSlash} />
                <span>{t("ada.dismiss.label")}</span>
              </button>
              <button
                className="ada-close-btn"
                onClick={toggleOpen}
                title={t("ada.close.title")}
                aria-label={t("ada.close.ariaLabel")}
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>{t("ada.close.label")}</span>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="ada-body">
            {/* Font Size Card */}
            <div
              className="ada-card ada-card--clickable"
              onClick={cycleScale}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? cycleScale() : undefined
              }
              title={t("ada.biggerText.title").replace(
                "{label}",
                current.label,
              )}
              aria-label={t("ada.biggerText.ariaLabel").replace(
                "{label}",
                current.label,
              )}
            >
              <div className="ada-card-label">{t("ada.card.biggerText")}</div>
              <div className="ada-font-display">
                <span className="ada-font-a-small">A</span>
                <span className="ada-font-a-large">A</span>
              </div>
              <div
                className={`ada-scale-track${scaleIndex === 0 ? " ada-scale-track--hidden" : ""}`}
                aria-label={t("ada.fontScale.ariaLabel")}
                onClick={(e) => e.stopPropagation()}
              >
                {SCALES.map((_, idx) => (
                  <button
                    key={idx}
                    className={`ada-scale-dot${idx === scaleIndex ? " ada-scale-dot--active" : ""}${idx < scaleIndex ? " ada-scale-dot--filled" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setScaleIndex(idx);
                    }}
                    aria-label={t("ada.setFontSize.ariaLabel").replace(
                      "{label}",
                      SCALES[idx].label,
                    )}
                    aria-current={idx === scaleIndex ? "true" : undefined}
                  />
                ))}
              </div>
            </div>

            {/* Contrast Card */}
            <div
              className="ada-card ada-card--clickable"
              onClick={toggleContrast}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " "
                  ? toggleContrast()
                  : undefined
              }
              title={t("ada.contrast.title").replace(
                "{theme}",
                isDark ? t("ada.contrast.dark") : t("ada.contrast.light"),
              )}
              aria-label={t("ada.contrast.ariaLabel").replace(
                "{theme}",
                (isDark
                  ? t("ada.contrast.dark")
                  : t("ada.contrast.light")
                ).toLowerCase(),
              )}
            >
              <div className="ada-card-label">{t("ada.card.contrast")}</div>
              <div className="ada-theme-circles">
                <span className="ada-theme-half-circle" aria-hidden="true" />
              </div>
            </div>

            {/* Text Spacing Card */}
            <div
              className="ada-card ada-card--clickable"
              onClick={cycleSpacing}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? cycleSpacing() : undefined
              }
              title={t("ada.textSpacing.title").replace(
                "{label}",
                t(SPACING_STEPS[spacingIndex].label),
              )}
              aria-label={t("ada.textSpacing.ariaLabel").replace(
                "{label}",
                t(SPACING_STEPS[spacingIndex].label),
              )}
            >
              <div className="ada-card-label">{t("ada.card.textSpacing")}</div>
              <div className="ada-icon-display" aria-hidden="true">
                <svg viewBox="0 0 48 28" className="ada-svg-icon">
                  <line
                    x1="2"
                    y1="8"
                    x2="46"
                    y2="8"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="2"
                    y1="16"
                    x2="46"
                    y2="16"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="2"
                    y1="24"
                    x2="46"
                    y2="24"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <polyline
                    points="6,4 2,8 6,12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <polyline
                    points="42,4 46,8 42,12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <div
                className={`ada-scale-track${spacingIndex === 0 ? " ada-scale-track--hidden" : ""}`}
                onClick={(e) => e.stopPropagation()}
              >
                {SPACING_STEPS.map((_, idx) => (
                  <button
                    key={idx}
                    className={`ada-scale-dot${idx === spacingIndex ? " ada-scale-dot--active" : ""}${idx < spacingIndex ? " ada-scale-dot--filled" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSpacingIndex(idx);
                    }}
                    aria-label={t("ada.setSpacing.ariaLabel").replace(
                      "{label}",
                      t(SPACING_STEPS[idx].label),
                    )}
                  />
                ))}
              </div>
            </div>

            {/* Line Height Card */}
            <div
              className="ada-card ada-card--clickable"
              onClick={cycleLineHeight}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " "
                  ? cycleLineHeight()
                  : undefined
              }
              title={t("ada.lineHeight.cardTitle").replace(
                "{label}",
                t(LINE_HEIGHT_STEPS[lineHeightIndex].label),
              )}
              aria-label={t("ada.lineHeight.cardAriaLabel").replace(
                "{label}",
                t(LINE_HEIGHT_STEPS[lineHeightIndex].label),
              )}
            >
              <div className="ada-card-label">{t("ada.card.lineHeight")}</div>
              <div className="ada-icon-display" aria-hidden="true">
                <svg viewBox="0 0 48 36" className="ada-svg-icon">
                  <line
                    x1="14"
                    y1="4"
                    x2="46"
                    y2="4"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="14"
                    y1="14"
                    x2="46"
                    y2="14"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="14"
                    y1="24"
                    x2="46"
                    y2="24"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="14"
                    y1="34"
                    x2="46"
                    y2="34"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="6"
                    y1="4"
                    x2="6"
                    y2="34"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <polyline
                    points="3,9 6,4 9,9"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <polyline
                    points="3,29 6,34 9,29"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <div
                className={`ada-scale-track${lineHeightIndex === 0 ? " ada-scale-track--hidden" : ""}`}
                onClick={(e) => e.stopPropagation()}
              >
                {LINE_HEIGHT_STEPS.map((_, idx) => (
                  <button
                    key={idx}
                    className={`ada-scale-dot${idx === lineHeightIndex ? " ada-scale-dot--active" : ""}${idx < lineHeightIndex ? " ada-scale-dot--filled" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLineHeightIndex(idx);
                    }}
                    aria-label={t("ada.setLineHeight.ariaLabel").replace(
                      "{label}",
                      t(LINE_HEIGHT_STEPS[idx].label),
                    )}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="ada-footer">
            <button
              className={`ada-reset-btn${scaleIndex !== 0 || spacingIndex !== 0 || lineHeightIndex !== 0 || isDark ? " ada-reset-btn--active" : ""}`}
              onClick={resetAll}
              aria-label={t("ada.resetAll.ariaLabel")}
            >
              {t("ada.resetAll")}
            </button>
            <span className="ada-footer-brand">{t("ada.footer.brand")}</span>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <div className="ada-fab-container">
        <button
          className={`ada-fab ${scaleIndex > 0 ? "ada-fab--active" : ""}`}
          onClick={toggleOpen}
          title={isOpen ? t("ada.fab.close") : t("ada.fab.open")}
          aria-label={isOpen ? t("ada.fab.close") : t("ada.fab.open")}
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <FontAwesomeIcon icon={faTimes} />
          ) : (
            <FontAwesomeIcon icon={faUniversalAccess} />
          )}
        </button>
        {!isOpen && <span className="ada-fab-label">{t("ada.fab.label")}</span>}
        {scaleIndex > 0 && !isOpen && (
          <span
            className="ada-fab-badge"
            aria-label={t("ada.fontSizeBadge.ariaLabel").replace(
              "{label}",
              current.label,
            )}
          >
            {current.label}
          </span>
        )}
      </div>
    </>
  );
}

export default ADAWidget;
